import api from "@/lib/api";
import { getInstitution, resultDocBody, RESULT_CSS } from "@/lib/resultDocument";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export async function printReportCard(studentId, studentName, track) {
  const [ov, ge, tg, wk] = await Promise.all([
    api.get(`/analytics/overview/${studentId}`),
    api.get(`/general-exams/${studentId}`),
    api.get(`/targets/${studentId}`),
    api.get(`/analytics/weak-topics/${studentId}`),
  ]);
  const overview = ov.data;
  const exams = [...ge.data].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const targets = tg.data;
  const weak = wk.data.topics || [];
  const weakSummary = { resources_done: wk.data.resources_done || 0, units_done: wk.data.units_done || 0 };

  const nets = (t) => exams.filter((e) => (e.exam_type || "").toUpperCase() === t).map((e) => Number(e.total_net || 0));
  const trend = (arr) => {
    if (arr.length < 2) return "—";
    const d = arr[arr.length - 1] - arr[0];
    return (d >= 0 ? "▲ +" : "▼ ") + d.toFixed(2) + " net";
  };
  const tytArr = nets("TYT");
  const aytArr = nets("AYT");

  const examRows = exams
    .map(
      (e) => `<tr>
        <td>${esc(e.date)}</td>
        <td><span class="tag ${e.exam_type === "TYT" ? "b" : "a"}">${esc(e.exam_type)}</span></td>
        <td>${esc(e.name || "-")}</td>
        <td class="num">${Number(e.total_net || 0).toFixed(2)}</td>
        <td class="num">${e.ranking ? Number(e.ranking).toLocaleString("tr-TR") : "-"}</td>
      </tr>`
    )
    .join("");

  const targetRows = targets
    .map(
      (t) => `<tr>
        <td>${esc(t.university)}</td>
        <td>${esc(t.department || "-")}</td>
        <td class="num">${Number(t.target_tyt_net || 0).toFixed(0)}</td>
        <td class="num">${Number(t.target_ayt_net || 0).toFixed(0)}</td>
      </tr>`
    )
    .join("");

  const weakRows = weak.length
    ? weak.map((w) => `<li><b>${esc(w.subject)}</b> — ${esc(w.unit)}${w.resource_name ? ` <span class="muted">(${esc(w.resource_name)})</span>` : ""} <span class="rate">%${w.error_rate}</span> <span class="muted">${w.wrong} yanlış / ${w.total} soru</span></li>`).join("")
    : `<li class="muted">Kritik hata oranına sahip konu yok.</li>`;
  const weakNote = `<p class="muted" style="margin:4px 0 8px">${weakSummary.resources_done} kaynak · ${weakSummary.units_done} ünite bitirildi — doğru/yanlış oranına göre otomatik belirlendi.</p>`;

  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Karne - ${esc(studentName)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:32px;background:#fff}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #2563eb;padding-bottom:16px;margin-bottom:24px}
    .brand{font-size:13px;color:#2563eb;font-weight:700;letter-spacing:1px;text-transform:uppercase}
    h1{font-size:26px;margin:4px 0 2px} .sub{color:#64748b;font-size:14px}
    .date{color:#94a3b8;font-size:12px;text-align:right}
    .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
    .kpi{border:1px solid #e2e8f0;border-radius:10px;padding:14px}
    .kpi .l{font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:.5px}
    .kpi .v{font-size:24px;font-weight:800;margin-top:4px}
    h2{font-size:15px;margin:22px 0 10px;color:#1e293b;border-left:4px solid #2563eb;padding-left:8px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th,td{text-align:left;padding:8px 10px;border-bottom:1px solid #e2e8f0}
    th{background:#f1f5f9;font-size:11px;text-transform:uppercase;color:#475569}
    td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
    .tag{padding:2px 8px;border-radius:20px;font-size:11px;font-weight:700}
    .tag.b{background:#dbeafe;color:#1d4ed8}.tag.a{background:#fef3c7;color:#b45309}
    .trend{display:flex;gap:16px;margin-bottom:8px}
    .trend .box{flex:1;border:1px solid #e2e8f0;border-radius:10px;padding:12px}
    .trend .t{font-size:12px;color:#64748b}.trend .n{font-size:18px;font-weight:800}
    ul{margin:0;padding-left:18px} li{margin:4px 0} .rate{color:#dc2626;font-weight:700}
    .muted{color:#94a3b8;list-style:none;margin-left:-18px}
    .foot{margin-top:32px;text-align:center;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:12px}
    @media print{body{padding:16px}}
  </style></head><body>
    <div class="head">
      <div>
        <div class="brand">TYT / AYT Takip Sistemi</div>
        <h1>${esc(studentName)}</h1>
        <div class="sub">${esc(track || "Alan belirtilmemiş")} · Gelişim Karnesi</div>
      </div>
      <div class="date">Oluşturma: ${new Date().toLocaleString("tr-TR")}</div>
    </div>

    <div class="kpis">
      <div class="kpi"><div class="l">Toplam Soru</div><div class="v">${overview.total_questions}</div></div>
      <div class="kpi"><div class="l">Doğru</div><div class="v" style="color:#059669">${overview.correct}</div></div>
      <div class="kpi"><div class="l">Yanlış</div><div class="v" style="color:#dc2626">${overview.wrong}</div></div>
      <div class="kpi"><div class="l">Toplam Net</div><div class="v" style="color:#4f46e5">${Number(overview.net).toFixed(1)}</div></div>
    </div>

    <h2>Deneme Net Gelişimi</h2>
    <div class="trend">
      <div class="box"><div class="t">Ortalama TYT Net</div><div class="n" style="color:#1d4ed8">${overview.avg_tyt_net} <small style="font-size:12px;color:#64748b">${trend(tytArr)}</small></div></div>
      <div class="box"><div class="t">Ortalama AYT Net</div><div class="n" style="color:#b45309">${overview.avg_ayt_net} <small style="font-size:12px;color:#64748b">${trend(aytArr)}</small></div></div>
    </div>

    <h2>Genel Deneme Sonuçları</h2>
    <table><thead><tr><th>Tarih</th><th>Sınav</th><th>Deneme</th><th class="num">Net</th><th class="num">Sıralama</th></tr></thead>
    <tbody>${examRows || '<tr><td colspan="5" style="color:#94a3b8">Deneme kaydı yok.</td></tr>'}</tbody></table>

    <h2>Hedef Bölümler</h2>
    <table><thead><tr><th>Üniversite</th><th>Bölüm</th><th class="num">Hedef TYT</th><th class="num">Hedef AYT</th></tr></thead>
    <tbody>${targetRows || '<tr><td colspan="4" style="color:#94a3b8">Hedef tanımlı değil.</td></tr>'}</tbody></table>

    <h2>Eksik & Yanlışı Çok Olan Konular</h2>
    ${weakNote}
    <ul>${weakRows}</ul>

    <div class="foot">Bu karne TYT/AYT Takip Sistemi tarafından otomatik oluşturulmuştur.</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

const DOC_CSS = `
  *{box-sizing:border-box} body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:32px;background:#fff}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid var(--ac);padding-bottom:16px;margin-bottom:24px}
  .brand{font-size:13px;color:var(--ac);font-weight:700;letter-spacing:1px;text-transform:uppercase}
  h1{font-size:24px;margin:4px 0 2px} .sub{color:#64748b;font-size:14px}
  .date{color:#94a3b8;font-size:12px;text-align:right}
  .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
  .kpi{border:1px solid #e2e8f0;border-radius:10px;padding:14px}
  .kpi .l{font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:.5px}
  .kpi .v{font-size:24px;font-weight:800;margin-top:4px}
  h2{font-size:15px;margin:22px 0 10px;color:#1e293b;border-left:4px solid var(--ac);padding-left:8px}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:8px}
  th,td{text-align:left;padding:8px 10px;border-bottom:1px solid #e2e8f0}
  th{background:#f1f5f9;font-size:11px;text-transform:uppercase;color:#475569}
  td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
  tr.tot{background:#f8fafc;font-weight:700}
  td.lft,th.lft{text-align:left}
  .g{color:#16a34a;font-weight:600}.r{color:#dc2626;font-weight:600}.o{color:#d97706;font-weight:600}.net{color:#2563eb;font-weight:700}
  .kblock{border:1px solid #e2e8f0;border-radius:10px;padding:12px;margin-bottom:12px}
  .khead{display:flex;justify-content:space-between;margin-bottom:8px}
  .ksub{color:#64748b;font-size:12px}
  .kgrid{display:flex;flex-wrap:wrap;gap:6px}
  .chip{display:inline-flex;align-items:center;gap:4px;border:1px solid #e2e8f0;border-radius:6px;padding:2px 6px;font-size:11px}
  .chip .qn{color:#94a3b8;font-weight:600}
  .chip .qm{font-weight:800}
  .chip .qz{color:#475569}
  .chip.ok{background:#ecfdf5;border-color:#a7f3d0}.chip.ok .qm{color:#059669}
  .chip.no{background:#fef2f2;border-color:#fecaca}.chip.no .qm{color:#dc2626}
  .chip.em{background:#f8fafc}.chip.em .qm{color:#94a3b8}
  .kwrong{margin-top:8px;font-size:12px;color:#b91c1c;background:#fef2f2;border-radius:6px;padding:6px 8px}
  .foot{margin-top:32px;text-align:center;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:12px}
  @media print{body{padding:16px}}
`;

function openDoc(title, ac, bodyHtml, noAutoPrint = false) {
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>:root{--ac:${ac}}${DOC_CSS}</style></head><body>${bodyHtml}
  <div class="foot">Bu belge TYT/AYT Takip Sistemi tarafından otomatik oluşturulmuştur.</div>
  ${noAutoPrint ? `<div style="position:fixed;right:16px;bottom:16px;display:flex;gap:8px"><button onclick="window.print()" style="background:var(--ac);color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:700;cursor:pointer">Yazdır / PDF</button></div><style>@media print{button{display:none}}</style>` : `<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>`}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

export async function printExamResult(studentId, studentName, track, examType) {
  const { data } = await api.get(`/general-exams/${studentId}`);
  const rows = data
    .filter((e) => (e.exam_type || "").toUpperCase() === examType)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  // Optik kaynaklı denemelerin detaylı belgelerini de yükle (satıra tıklayınca açılır)
  let detailMap = {};
  try {
    const opt = await api.get(`/optical-results/${studentId}`);
    const byExam = Object.fromEntries(opt.data.map((o) => [o.exam_id, o]));
    const inst = await getInstitution();
    const pairs = await Promise.all(rows.filter((r) => r.optical_exam_id && byExam[r.optical_exam_id]).map(async (r) => {
      const d = await api.get(`/optical-results/${studentId}/document/${byExam[r.optical_exam_id].id}`);
      return [r.optical_exam_id, resultDocBody(d.data, inst)];
    }));
    detailMap = Object.fromEntries(pairs);
  } catch { detailMap = {}; }
  const nets = rows.map((r) => Number(r.total_net || 0));
  const avg = nets.length ? (nets.reduce((a, b) => a + b, 0) / nets.length).toFixed(2) : "0.00";
  const best = nets.length ? Math.max(...nets).toFixed(2) : "0.00";
  const last = nets.length ? nets[nets.length - 1].toFixed(2) : "0.00";
  const trend = nets.length >= 2 ? (nets[nets.length - 1] - nets[0]) : 0;
  const trendTxt = nets.length >= 2 ? `${trend >= 0 ? "▲ +" : "▼ "}${trend.toFixed(2)}` : "—";
  const ac = examType === "TYT" ? "#2563eb" : "#d97706";
  const body = rows
    .map((e, i) => {
      const has = !!detailMap[e.optical_exam_id];
      const nm = has ? `<a href="#det-${i}" style="color:var(--ac);font-weight:700;text-decoration:underline">${esc(e.name || "-")}</a> <span style="font-size:10px;color:#64748b">(detay ↓)</span>` : esc(e.name || "-");
      return `<tr${has ? ` style="cursor:pointer" onclick="document.getElementById('det-${i}').scrollIntoView({behavior:'smooth'})"` : ""}><td>${esc(e.date)}</td><td>${nm}</td><td class="num">${Number(e.total_net || 0).toFixed(2)}</td><td class="num">${e.ranking ? Number(e.ranking).toLocaleString("tr-TR") : "-"}</td></tr>`;
    })
    .join("");
  const details = rows.map((e, i) => (detailMap[e.optical_exam_id] ? `<div id="det-${i}" style="page-break-before:always;margin-top:24px">${detailMap[e.optical_exam_id]}</div>` : "")).join("");
  const html = `
    <div class="head"><div><div class="brand">TYT / AYT Takip Sistemi</div>
      <h1>${examType} Sonuç Belgesi</h1>
      <div class="sub">${esc(studentName)} · ${esc(track || "Alan belirtilmemiş")}</div></div>
      <div class="date">Oluşturma: ${new Date().toLocaleString("tr-TR")}</div></div>
    <div class="kpis">
      <div class="kpi"><div class="l">Deneme Sayısı</div><div class="v">${rows.length}</div></div>
      <div class="kpi"><div class="l">Ortalama Net</div><div class="v" style="color:${ac}">${avg}</div></div>
      <div class="kpi"><div class="l">En Yüksek</div><div class="v" style="color:#059669">${best}</div></div>
      <div class="kpi"><div class="l">Son / Gelişim</div><div class="v">${last} <small style="font-size:12px;color:#64748b">${trendTxt}</small></div></div>
    </div>
    <h2>${examType} Deneme Sonuçları</h2>
    <table><thead><tr><th>Tarih</th><th>Deneme</th><th class="num">Net</th><th class="num">Sıralama</th></tr></thead>
    <tbody>${body || `<tr><td colspan="4" style="color:#94a3b8">${examType} deneme kaydı yok.</td></tr>`}</tbody></table>
    ${details ? `<style>${RESULT_CSS}</style>${details}` : ""}`;
  return openDoc(`${examType} Sonuç - ${studentName}`, ac, html, !!details);
}

export async function printBranchResult(studentId, studentName, track) {
  const { data } = await api.get(`/branch-exams/${studentId}`);
  const branches = [...new Set(data.map((i) => i.branch || "Diğer"))];
  const sections = branches
    .map((b) => {
      const rows = data.filter((i) => (i.branch || "Diğer") === b).sort((a, z) => (a.date || "").localeCompare(z.date || ""));
      const tot = rows.reduce((a, r) => a + Number(r.total || 0), 0);
      const cor = rows.reduce((a, r) => a + Number(r.correct || 0), 0);
      const wr = rows.reduce((a, r) => a + Number(r.wrong || 0), 0);
      const bl = rows.reduce((a, r) => a + Number(r.blank || 0), 0);
      const body = rows
        .map((r) => `<tr><td>${esc(r.date)}</td><td>${esc(r.name || "-")}</td><td class="num">${r.total}</td><td class="num">${r.correct}</td><td class="num">${r.wrong}</td><td class="num">${r.blank ?? 0}</td><td class="num">${Number(r.net || 0).toFixed(2)}</td></tr>`)
        .join("");
      return `<h2>${esc(b)} — ${rows.length} Deneme</h2>
        <table><thead><tr><th>Tarih</th><th>Deneme</th><th class="num">Toplam</th><th class="num">Doğru</th><th class="num">Yanlış</th><th class="num">Boş</th><th class="num">Net</th></tr></thead>
        <tbody>${body}<tr class="tot"><td colspan="2">Toplam</td><td class="num">${tot}</td><td class="num">${cor}</td><td class="num">${wr}</td><td class="num">${bl}</td><td class="num">${(cor - wr / 4).toFixed(2)}</td></tr></tbody></table>`;
    })
    .join("");
  const html = `
    <div class="head"><div><div class="brand">TYT / AYT Takip Sistemi</div>
      <h1>Branş Deneme Sonuç Belgesi</h1>
      <div class="sub">${esc(studentName)} · ${esc(track || "Alan belirtilmemiş")}</div></div>
      <div class="date">Oluşturma: ${new Date().toLocaleString("tr-TR")}</div></div>
    ${sections || '<p style="color:#94a3b8">Branş denemesi kaydı yok.</p>'}`;
  return openDoc(`Branş Sonuç - ${studentName}`, "#7c3aed", html);
}

export function printBulkResult(track, students, kind) {
  const isBranch = kind === "BRANS";
  const ac = kind === "TYT" ? "#2563eb" : kind === "AYT" ? "#d97706" : "#7c3aed";
  const label = isBranch ? "Branş Deneme" : `${kind} Deneme`;
  const stat = (s) => {
    if (isBranch) {
      const rows = s.branch || [];
      const nets = rows.map((r) => Number(r.net || 0));
      return { count: rows.length, avg: nets.length ? (nets.reduce((a, b) => a + b, 0) / nets.length) : 0, best: nets.length ? Math.max(...nets) : 0, last: nets.length ? Number(rows[rows.length - 1].net || 0) : 0 };
    }
    const rows = (s.general || []).filter((e) => (e.exam_type || "").toUpperCase() === kind);
    const nets = rows.map((r) => Number(r.total_net || 0));
    return { count: rows.length, avg: nets.length ? (nets.reduce((a, b) => a + b, 0) / nets.length) : 0, best: nets.length ? Math.max(...nets) : 0, last: nets.length ? Number(rows[rows.length - 1].total_net || 0) : 0 };
  };
  const enriched = students.map((s) => ({ name: s.name, ...stat(s) })).sort((a, b) => b.avg - a.avg);
  const clsAvg = enriched.length ? (enriched.reduce((a, s) => a + s.avg, 0) / enriched.length).toFixed(2) : "0.00";
  const body = enriched
    .map((s, i) => `<tr><td class="num">${i + 1}</td><td>${esc(s.name)}</td><td class="num">${s.count}</td><td class="num" style="color:${ac};font-weight:700">${s.avg.toFixed(2)}</td><td class="num">${s.best.toFixed(2)}</td><td class="num">${s.last.toFixed(2)}</td></tr>`)
    .join("");
  const html = `
    <div class="head"><div><div class="brand">TYT / AYT Takip Sistemi</div>
      <h1>${label} Toplu Sonuç Belgesi</h1>
      <div class="sub">${esc(track)} · ${enriched.length} öğrenci · Sınıf ort. net ${clsAvg}</div></div>
      <div class="date">Oluşturma: ${new Date().toLocaleString("tr-TR")}</div></div>
    <table><thead><tr><th class="num">#</th><th>Öğrenci</th><th class="num">Deneme</th><th class="num">Ort. Net</th><th class="num">En Yüksek</th><th class="num">Son</th></tr></thead>
    <tbody>${body || '<tr><td colspan="6" style="color:#94a3b8">Kayıt yok.</td></tr>'}</tbody></table>`;
  return openDoc(`${label} Toplu - ${track}`, ac, html);
}

export function printOpticalCard(r, studentName, track) {
  const subs = r.per_subject || [];
  const t = r.totals || {};
  const pct = (dc, ss) => (ss ? Math.round((dc / ss) * 100) : 0);

  // 1. Ders Başarı Analizi Tablosu
  const subjRows = subs
    .map((s) => `<tr>
      <td class="lft" style="font-weight:700">${esc(s.name)}</td>
      <td class="num">${s.ss}</td>
      <td class="num g">${s.dc}</td>
      <td class="num r">${s.yc}</td>
      <td class="num o">${s.blank}</td>
      <td class="num net" style="font-size:14px">${Number(s.net).toFixed(2)}</td>
      <td class="num" style="font-weight:700">%${pct(s.dc, s.ss)}</td>
    </tr>`)
    .join("");

  const totRow = `<tr class="tot">
    <td class="lft">GENEL TOPLAM</td>
    <td class="num">${t.ss || 0}</td>
    <td class="num g">${t.dc || 0}</td>
    <td class="num r">${t.yc || 0}</td>
    <td class="num o">${t.blank || 0}</td>
    <td class="num net" style="font-size:15px;color:#0f766e">${Number(t.net || 0).toFixed(2)}</td>
    <td class="num">%${pct(t.dc || 0, t.ss || 0)}</td>
  </tr>`;

  // 2. Ders ve Soru Bazında Detaylı Kazanım Tabloları (Dersin Adı, Soru No, Kazanım, D/Y/B)
  const kazTables = subs
    .map((s) => {
      const marksArr = String(s.marks || "").split("");
      const studentAns = (r.answers?.[s.name] || "").toUpperCase();

      const qRows = marksArr
        .map((m, i) => {
          const qNum = i + 1;
          const kaz = (s.kazanimlar || [])[i] || `Soru ${qNum}`;
          const stu = studentAns[i] || "-";
          const statusCls = m === "D" ? "g" : m === "Y" ? "r" : "o";
          const statusBadge = m === "D"
            ? '<span style="color:#059669;font-weight:800;background:#ecfdf5;padding:2px 8px;border-radius:4px;border:1px solid #a7f3d0">D (Doğru)</span>'
            : m === "Y"
            ? '<span style="color:#dc2626;font-weight:800;background:#fef2f2;padding:2px 8px;border-radius:4px;border:1px solid #fecaca">Y (Yanlış)</span>'
            : '<span style="color:#d97706;font-weight:800;background:#fffbeb;padding:2px 8px;border-radius:4px;border:1px solid #fde68a">B (Boş)</span>';

          return `<tr>
            <td style="font-weight:700;text-align:center">${qNum}</td>
            <td style="font-weight:600;color:#1e293b">${esc(kaz)}</td>
            <td style="text-align:center;font-family:monospace;font-weight:800">${esc(stu)}</td>
            <td style="text-align:center">${statusBadge}</td>
          </tr>`;
        })
        .join("");

      const wrongList = marksArr
        .map((m, i) => (m === "Y" ? (s.kazanimlar || [])[i] || `Soru ${i + 1}` : null))
        .filter(Boolean);

      const blankList = marksArr
        .map((m, i) => (m === "B" ? (s.kazanimlar || [])[i] || `Soru ${i + 1}` : null))
        .filter(Boolean);

      return `
        <div style="margin-top:20px;page-break-inside:avoid">
          <div style="display:flex;justify-content:space-between;align-items:center;background:#f8fafc;padding:8px 12px;border:1px solid #e2e8f0;border-left:5px solid #0f766e;border-radius:6px;margin-bottom:6px">
            <h3 style="margin:0;font-size:14px;color:#0f172a">${esc(s.name)} — Soru & Kazanım Listesi</h3>
            <span style="font-size:12px;color:#475569;font-weight:600">
              ${s.dc} Doğru · ${s.yc} Yanlış · ${s.blank} Boş · Net: <b style="color:#0f766e">${Number(s.net).toFixed(2)}</b>
            </span>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:50px;text-align:center">Soru</th>
                <th>Kazanım Adı</th>
                <th style="width:80px;text-align:center">Öğrenci</th>
                <th style="width:110px;text-align:center">Durum</th>
              </tr>
            </thead>
            <tbody>${qRows}</tbody>
          </table>

          ${
            wrongList.length > 0
              ? `<div style="background:#fef2f2;border:1px solid #fecaca;color:#991b1b;padding:8px 12px;border-radius:6px;font-size:12px;margin-top:6px">
                  <b>Tekrar Edilmesi Gereken Yanlış Kazanımlar:</b> ${wrongList.map((w) => esc(w)).join(" · ")}
                </div>`
              : ""
          }
          ${
            blankList.length > 0
              ? `<div style="background:#fffbeb;border:1px solid #fde68a;color:#92400e;padding:6px 12px;border-radius:6px;font-size:11px;margin-top:4px">
                  <b>Boş Bırakılan Kazanımlar:</b> ${blankList.map((b) => esc(b)).join(" · ")}
                </div>`
              : ""
          }
        </div>
      `;
    })
    .join("");

  const html = `
    <div class="head">
      <div>
        <div class="brand">TYT / AYT Takip Sistemi · Canlı Optik Sınav</div>
        <h1>${esc(r.exam_name || "Deneme")} — Resmi Sınav Sonuç Belgesi</h1>
        <div class="sub">${esc(studentName)} · ${esc(track || "Alan Belirtilmemiş")} · ${esc(r.exam_type || "")} · Tarih: ${esc(r.date || "")}</div>
      </div>
      <div class="date">Belge Tarihi: ${new Date().toLocaleString("tr-TR")}</div>
    </div>

    <div class="kpis">
      <div class="kpi"><div class="l">Toplam Net</div><div class="v" style="color:#0f766e">${Number(t.net || 0).toFixed(2)}</div></div>
      <div class="kpi"><div class="l">Toplam Doğru (D)</div><div class="v" style="color:#059669">${t.dc || 0}</div></div>
      <div class="kpi"><div class="l">Toplam Yanlış (Y)</div><div class="v" style="color:#dc2626">${t.yc || 0}</div></div>
      <div class="kpi"><div class="l">Toplam Boş (B)</div><div class="v" style="color:#d97706">${t.blank || 0}</div></div>
    </div>

    <h2>1. Derslere Göre Başarı Analizi</h2>
    <table>
      <thead>
        <tr>
          <th>Ders Adı</th>
          <th class="num">Soru (SS)</th>
          <th class="num">Doğru (D)</th>
          <th class="num">Yanlış (Y)</th>
          <th class="num">Boş (B)</th>
          <th class="num">Net</th>
          <th class="num">Başarı %</th>
        </tr>
      </thead>
      <tbody>
        ${subjRows}
        ${totRow}
      </tbody>
    </table>

    <h2>2. Derslere Ait Kazanım & Başarı Değerlendirmesi</h2>
    <p style="font-size:12px;color:#64748b;margin:0 0 10px">Her sorunun karşısında admin tarafından tanımlanan kazanım bilgisi ve öğrencinin sınav performansı (D = Doğru, Y = Yanlış, B = Boş) gösterilmektedir.</p>
    ${kazTables}
  `;

  return openDoc(`${r.exam_name || "Optik Sonuc"} - ${studentName}`, "#0f766e", html);
}

export async function printDetailedCard(card) {
  const ex = card.info || {};
  const t = card.exam_type || "TYT";
  const s = card.student || {};
  const acc = t === "AYT" ? "#8a6d3b" : "#6b5b6e";
  const cell = (l, v) => `<div class="ic"><div class="il">${esc(l)}</div><div class="iv">${esc(v || "-")}</div></div>`;
  const num = (v, d = 2) => (v === null || v === undefined || v === "" ? "-" : Number(v).toFixed(d));
  const rowsHtml = (card.rows || [])
    .map((r) => `<tr>
      <td class="lft">${esc(r.subject)}</td>
      <td>${r.ss}</td><td class="g">${r.dc}</td><td class="r">${r.yc}</td><td class="o">${r.blank}</td>
      <td class="net">${num(r.net)}</td>
      <td>${num(r.gnort)}</td><td>${r.sbort === null ? "-" : num(r.sbort)}</td><td>${r.snort === null ? "-" : num(r.snort)}</td>
      <td>${r.gensr ?? "-"}</td><td>${r.sbsr ?? "-"}</td><td>${r.snsr ?? "-"}</td></tr>`)
    .join("");
  const tot = card.totals || {};
  const totHtml = `<tr class="tot">
      <td class="lft">Toplam</td>
      <td>${tot.ss || 0}</td><td class="g">${tot.dc || 0}</td><td class="r">${tot.yc || 0}</td><td class="o">${tot.blank || 0}</td>
      <td class="net">${num(tot.net)}</td>
      <td>${num(tot.gnort)}</td><td>${tot.sbort === null ? "-" : num(tot.sbort)}</td><td>${tot.snort === null ? "-" : num(tot.snort)}</td>
      <td></td><td></td><td></td></tr>`;
  const p = card.puan || {};
  const rk = card.ranks || {};
  const kt = card.katilim || {};
  const answersHtml = (card.answers || [])
    .map((a) => {
      const ruler = a.key ? [...a.key.replace(/\s/g, "")].map((_, i) => ((i + 1) % 10 || 0)).join("") : "";
      return `<div class="ansblk">
        <table class="ans"><tr class="ah"><td class="alabel">${esc(a.label)}</td><td class="ruler">${ruler}</td></tr>
        <tr><td class="alft">Doğru Cevaplar</td><td class="mono">${esc(a.key)}</td></tr>
        <tr><td class="alft">Öğrenci Cevapları</td><td class="mono">${esc(a.student)}</td></tr>
        <tr><td class="alft">Sonuç</td><td class="mono res">${esc(a.marks)}</td></tr></table></div>`;
    })
    .join("");
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${t} Sonuç Karnesi - ${esc(s.name)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;margin:0;padding:24px;background:#fff;font-size:12px}
    .banner{display:flex;align-items:stretch;margin-bottom:14px}
    .arrow{flex:1;background:linear-gradient(90deg,${acc},#4a3f4e);color:#fff;position:relative;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:800;letter-spacing:2px;padding:14px;clip-path:polygon(0 0,96% 0,100% 50%,96% 100%,0 100%,3% 50%)}
    .arrow::before{content:"";position:absolute;left:8px;top:50%;transform:translateY(-50%);border-left:14px solid #c0392b;border-top:11px solid transparent;border-bottom:11px solid transparent}
    .htitle{width:280px;display:flex;align-items:center;padding-left:22px;font-size:18px;color:#374151;font-weight:600}
    .topgrid{display:flex;gap:8px;margin-bottom:16px;align-items:stretch}
    .logo{width:120px;border:1px solid #1e293b;background:#0f1e3d;color:#fff;border-radius:4px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px;text-align:center}
    .logo .lz{font-size:24px;font-weight:900;letter-spacing:2px}.logo .la{font-size:8px;letter-spacing:4px;margin-top:2px}
    .infowrap{flex:1;display:grid;grid-template-columns:1.6fr 1.2fr .7fr .8fr;gap:8px}
    .ic{background:#eef0f8;border:1px solid #dfe3f0;border-radius:4px;padding:7px 10px;text-align:center}
    .ic .il{font-size:10px;color:#6b7280}.ic .iv{font-size:13px;font-weight:700;margin-top:3px}
    .sect{display:flex;align-items:center;gap:6px;color:#c0392b;font-weight:700;font-size:13px;margin:14px 0 8px}
    .sect::before{content:"";border-left:9px solid #c0392b;border-top:7px solid transparent;border-bottom:7px solid transparent}
    .cols{display:flex;gap:16px;align-items:flex-start}
    .lcol{flex:1.55}.rcol{flex:1}
    table{width:100%;border-collapse:collapse}
    .grade th,.grade td{border:1px solid #e5e7eb;padding:5px 4px;text-align:center;font-size:11px}
    .grade th{background:#f3f4f6;color:#374151;font-weight:600}
    .grade td.lft{text-align:left;padding-left:8px;color:#374151}
    .grade td.g{color:#16a34a;font-weight:600}.grade td.r{color:#dc2626;font-weight:600}.grade td.o{color:#d97706;font-weight:600}
    .grade td.net{color:#2563eb;font-weight:700}
    .grade tr.tot td{background:#ecfdf5;font-weight:800;border-top:2px solid #10b981}
    .prank th,.prank td{border:1px solid #e5e7eb;padding:7px 8px;text-align:center;font-size:11px}
    .prank th{background:#f3f4f6;color:#374151}
    .prank td.big{font-size:15px;font-weight:800;color:#1e293b}
    .prank td.lft{text-align:left;font-weight:700}
    .mb{margin-bottom:14px}
    .ansblk{border:1px solid #e5e7eb;border-radius:6px;margin-bottom:10px;overflow:hidden}
    .ans td{border-bottom:1px solid #eef2f7;padding:6px 10px;font-size:11px}
    .ans .ah td{background:#f8fafc;font-weight:700}
    .ans .alabel{width:150px;color:#0f172a}
    .ans .alft{width:150px;color:#475569;background:#fafbfc}
    .ans .mono,.ans .ruler{font-family:'Courier New',monospace;letter-spacing:2px;color:#334155}
    .ans .res{letter-spacing:3px;font-weight:700}
    @media print{body{padding:10px}}
  </style></head><body>
    <div class="banner"><div class="arrow">${esc(t)}</div><div class="htitle">Detaylı Sonuç Karnesi</div></div>
    <div class="topgrid">
      <div class="logo"><div class="lz">ZOR</div><div class="la">AKADEMİ</div></div>
      <div class="infowrap">
        ${cell("Öğrenci Adı", s.name)}${cell("Sınıfı", s.sinif)}${cell("No", ex.no)}${cell("Kitapçık Türü", ex.booklet)}
        ${cell("Sınav Adı", ex.name)}${cell("Şube", ex.sube)}${cell("Sınav Kodu", ex.exam_code)}${cell("Sınav Tarihi", ex.date)}
      </div>
    </div>
    <div class="cols">
      <div class="lcol">
        <div class="sect">Ders Başarı ve Sıralama Bilgileri</div>
        <table class="grade">
          <thead><tr><th>Ders</th><th>SS</th><th>DC</th><th>YC</th><th>Boş</th><th>Net</th><th>GnOrt</th><th>ŞbOrt</th><th>SnOrt</th><th>GenSr</th><th>ŞbSr</th><th>SnSr</th></tr></thead>
          <tbody>${rowsHtml}${totHtml}</tbody>
        </table>
      </div>
      <div class="rcol">
        <div class="sect">Puan ve Sıralama Bilgileri</div>
        <table class="prank mb"><thead><tr><th>Puan Türü</th><th>Öğrenci Puan</th><th>Genel Puan Ort.</th><th>Yüzdelik Dilim</th></tr></thead>
          <tbody><tr><td class="lft">${esc(t)}</td><td class="big">${num(p.ogrenci, 3)}</td><td>${num(p.genel_ort, 3)}</td><td>%${num(p.yuzdelik)}</td></tr></tbody></table>
        <table class="prank"><thead><tr><th>Puan Türü</th><th>Genel Sr</th><th>İl Sr</th><th>İlçe Sr</th><th>Şube Sr</th><th>Sınıf Sr</th></tr></thead>
          <tbody>
            <tr><td class="lft">${esc(t)}</td><td>${rk.gen_sr ?? "-"}</td><td>${rk.il_sr ?? "-"}</td><td>${rk.ilce_sr ?? "-"}</td><td>${rk.sube_sr ?? "-"}</td><td>${rk.sinif_sr ?? "-"}</td></tr>
            <tr><td class="lft">Katılım</td><td>${kt.gen ?? "-"}</td><td>${kt.il ?? "-"}</td><td>${kt.ilce ?? "-"}</td><td>${kt.sube ?? "-"}</td><td>${kt.sinif ?? "-"}</td></tr>
          </tbody></table>
      </div>
    </div>
    ${answersHtml ? `<div class="sect">Cevap Analizleri</div>${answersHtml}` : ""}
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

export function printClassReport(rows) {
  const esc2 = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const bar = (p) => {
    const col = p >= 75 ? "#059669" : p >= 40 ? "#d97706" : "#dc2626";
    return `<div class="pbar"><div style="width:${p}%;background:${col}"></div></div>`;
  };
  const cards = rows
    .map(
      (r) => `<div class="cbox">
        <div class="ct">${esc2(r.track)} <span class="cn">${r.students} öğrenci</span></div>
        <div class="crow"><span>Ort. Net</span><b style="color:#4f46e5">${r.avg_net}</b></div>
        <div class="crow"><span>Deneme</span><b>${r.exam_count}</b></div>
        <div class="crow"><span>Tamamlanma</span><b>%${r.completion}</b></div>
        ${bar(r.completion)}
      </div>`
    )
    .join("");
  const trows = rows
    .map(
      (r) => `<tr>
        <td>${esc2(r.track)}</td>
        <td class="num">${r.students}</td>
        <td class="num" style="color:#4f46e5;font-weight:700">${r.avg_net}</td>
        <td class="num">${r.exam_count}</td>
        <td class="num">%${r.completion}</td>
        <td class="num">${r.done}/${r.total}</td>
      </tr>`
    )
    .join("");
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Sınıf Raporu</title>
  <style>
    *{box-sizing:border-box} body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:32px;background:#fff}
    .head{border-bottom:3px solid #2563eb;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-start}
    .brand{font-size:13px;color:#2563eb;font-weight:700;letter-spacing:1px;text-transform:uppercase}
    h1{font-size:24px;margin:4px 0 2px} .date{color:#94a3b8;font-size:12px}
    .cards{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:24px}
    .cbox{border:1px solid #e2e8f0;border-radius:10px;padding:14px}
    .ct{font-weight:800;font-size:15px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center}
    .cn{font-size:11px;color:#64748b;font-weight:600}
    .crow{display:flex;justify-content:space-between;font-size:13px;padding:2px 0;color:#475569}
    .pbar{height:8px;border-radius:6px;background:#f1f5f9;overflow:hidden;margin-top:8px}.pbar>div{height:100%}
    h2{font-size:15px;margin:22px 0 10px;color:#1e293b;border-left:4px solid #2563eb;padding-left:8px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th,td{text-align:left;padding:8px 10px;border-bottom:1px solid #e2e8f0}
    th{background:#f1f5f9;font-size:11px;text-transform:uppercase;color:#475569}
    td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
    .foot{margin-top:32px;text-align:center;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:12px}
    @media print{body{padding:16px}}
  </style></head><body>
    <div class="head">
      <div><div class="brand">TYT / AYT Takip Sistemi</div><h1>Sınıf / Alan Karşılaştırma Raporu</h1></div>
      <div class="date">Oluşturma: ${new Date().toLocaleString("tr-TR")}</div>
    </div>
    <div class="cards">${cards}</div>
    <h2>Özet Tablo</h2>
    <table><thead><tr><th>Sınıf / Alan</th><th class="num">Öğrenci</th><th class="num">Ort. Net</th><th class="num">Deneme</th><th class="num">Tamamlanma</th><th class="num">Görev</th></tr></thead>
    <tbody>${trows || '<tr><td colspan="6" style="color:#94a3b8">Veri yok.</td></tr>'}</tbody></table>
    <div class="foot">Bu rapor TYT/AYT Takip Sistemi tarafından otomatik oluşturulmuştur.</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
